<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import {
  buildTrafficPeriodQuery,
  formatDuration,
  TRAFFIC_PRESETS,
  type TrafficPeriodSelection,
  type TrafficPreset,
  type TrafficCustomEventAnalytics,
  type TrafficPageAnalytics,
  type TrafficSourceAnalytics,
  type TrafficVisitorAnalytics
} from '@xjlv2/api-client'
import { useBehaviorAnalytics, type BehaviorAnalyticsSubview } from '../../composables/useBehaviorAnalytics'
import type { TrafficWorkbenchApi } from '../../types'

const props = defineProps<{
  createApiClient: () => TrafficWorkbenchApi
  canView: boolean
}>()

const {
  data: analyticsData,
  loading: analyticsLoading,
  error: analyticsError,
  subview,
  period,
  featureEnabled,
  load: loadBehaviorAnalytics,
  reset: resetBehaviorAnalytics
} = useBehaviorAnalytics({
  createApiClient: props.createApiClient,
  canView: () => props.canView
})

watch(
  () => props.canView,
  (canView) => {
    if (canView) void loadBehaviorAnalytics()
    else resetBehaviorAnalytics()
  },
  { immediate: true }
)

const PRESET_LABELS: Record<TrafficPreset, string> = {
  TODAY: '今日',
  YESTERDAY: '昨日',
  LAST_7_DAYS: '近 7 天',
  LAST_30_DAYS: '近 30 天',
  THIS_MONTH: '本月',
  LAST_MONTH: '上月',
  THIS_YEAR: '本年',
  LAST_YEAR: '去年',
  CUSTOM: '自定义'
}

const presetOptions = TRAFFIC_PRESETS.map((value) => ({ value, label: PRESET_LABELS[value] }))
const moduleOptions: Array<{ value: string; label: string }> = [
  { value: '', label: '全部模块' },
  { value: 'content', label: '内容' },
  { value: 'mall', label: '商城' },
  { value: 'ticketing', label: '票务' }
]

const subviews: Array<{ key: BehaviorAnalyticsSubview; label: string }> = [
  { key: 'sources', label: '来源' },
  { key: 'visitors', label: '访客' },
  { key: 'pages', label: '受访页' },
  { key: 'events', label: '事件' }
]

const activeSubviewLabel = computed(
  () => subviews.find((view) => view.key === subview.value)?.label ?? ''
)

// 与 useBehaviorAnalytics 的默认周期保持一致，避免控件与服务端周期漂移。
const selectedPreset = ref<TrafficPreset>('THIS_MONTH')
const customFrom = ref('')
const customUntil = ref('')
const periodError = ref('')
const selectedModule = ref('')

watch(period, (next) => {
  if (next.preset !== 'CUSTOM') {
    selectedPreset.value = next.preset
    return
  }
  selectedPreset.value = 'CUSTOM'
  if (next.from) customFrom.value = next.from
  if (next.until) customUntil.value = next.until
})

function changePreset() {
  periodError.value = ''
  if (selectedPreset.value !== 'CUSTOM') {
    void loadBehaviorAnalytics({ period: { preset: selectedPreset.value } })
  }
}

function submitCustomPeriod() {
  if (selectedPreset.value !== 'CUSTOM') return
  const customPeriod: TrafficPeriodSelection = {
    preset: 'CUSTOM',
    from: customFrom.value,
    until: customUntil.value
  }
  try {
    buildTrafficPeriodQuery(customPeriod)
    periodError.value = ''
    void loadBehaviorAnalytics({ period: customPeriod })
  } catch (cause) {
    periodError.value = cause instanceof Error && cause.message.includes('366')
      ? '自定义周期不能超过 366 天'
      : '请选择有效的开始和结束日期'
  }
}

function changeModule() {
  void loadBehaviorAnalytics({ moduleCode: selectedModule.value || null })
}

function switchSubview(next: BehaviorAnalyticsSubview) {
  if (next === subview.value) return
  void loadBehaviorAnalytics({ subview: next })
}

function subviewData<T>(expected: BehaviorAnalyticsSubview): T | null {
  return subview.value === expected ? (analyticsData.value as T | null) : null
}

const sourcesView = computed(() => {
  const payload = subviewData<TrafficSourceAnalytics>('sources')
  if (!payload) return null
  const maxPv = maxMetric(payload.rows.map((row) => row.pv))
  return {
    totals: [
      { key: 'pv', label: '浏览量（PV）', value: formatCount(payload.totalPv) },
      { key: 'sessions', label: '访问次数', value: formatCount(payload.totalSessions) },
      { key: 'uv', label: '访客数（UV·近似）', value: formatCount(payload.totalUvApprox) }
    ],
    rows: payload.rows.map((row, index) => ({
      key: `${row.referrerKind}|${row.referrerHost}|${row.utmSource}|${index}`,
      referrerKind: row.referrerKind,
      referrerHost: row.referrerHost || '—',
      utmSource: row.utmSource || '—',
      pv: row.pv,
      sessions: row.sessions,
      uv: row.uv,
      pvWidth: barWidth(row.pv, maxPv)
    }))
  }
})

const visitorsView = computed(() => {
  const payload = subviewData<TrafficVisitorAnalytics>('visitors')
  if (!payload) return null
  const maxVisitors = maxMetric(payload.rows.map((row) => row.visitors))
  return {
    totals: [
      { key: 'visitors', label: '访客数（近似）', value: formatCount(payload.totalVisitorsApprox) },
      { key: 'sessions', label: '访问次数', value: formatCount(sumMetric(payload.rows, (row) => row.sessions)) },
      { key: 'pv', label: '浏览量（PV）', value: formatCount(sumMetric(payload.rows, (row) => row.pv)) }
    ],
    rows: payload.rows.map((row, index) => ({
      key: `${row.clientType}|${row.deviceOs}|${row.deviceClass}|${row.geoProvince}|${row.visitorCohort}|${index}`,
      clientType: row.clientType,
      deviceOs: row.deviceOs,
      deviceClass: row.deviceClass,
      geoProvince: row.geoProvince || '—',
      visitorCohort: row.visitorCohort === 'NEW' ? '新访客' : '回访客',
      visitors: row.visitors,
      sessions: row.sessions,
      pv: row.pv,
      visitorsWidth: barWidth(row.visitors, maxVisitors)
    }))
  }
})

const pagesView = computed(() => {
  const payload = subviewData<TrafficPageAnalytics>('pages')
  if (!payload) return null
  const maxPv = maxMetric(payload.rows.map((row) => row.pv))
  return {
    totals: [
      { key: 'pv', label: '浏览量（PV）', value: formatCount(sumMetric(payload.rows, (row) => row.pv)) },
      { key: 'uv', label: '访客数（UV·近似）', value: formatCount(sumMetric(payload.rows, (row) => row.uv)) },
      { key: 'entries', label: '进入次数', value: formatCount(sumMetric(payload.rows, (row) => row.entries)) },
      { key: 'exits', label: '退出次数', value: formatCount(sumMetric(payload.rows, (row) => row.exits)) }
    ],
    rows: payload.rows.map((row, index) => ({
      key: `${row.pageCode}|${index}`,
      pageCode: row.pageCode,
      pv: row.pv,
      uv: row.uv,
      averageActive: formatDuration(row.averageActiveMillis),
      entries: row.entries,
      exits: row.exits,
      pvWidth: barWidth(row.pv, maxPv)
    }))
  }
})

const eventsView = computed(() => {
  const payload = subviewData<TrafficCustomEventAnalytics>('events')
  if (!payload) return null
  const maxCount = maxMetric(payload.rows.map((row) => row.eventCount))
  return {
    totals: [
      { key: 'count', label: '事件次数', value: formatCount(sumMetric(payload.rows, (row) => row.eventCount)) },
      { key: 'uv', label: '触发访客（近似）', value: formatCount(sumMetric(payload.rows, (row) => row.uv)) }
    ],
    rows: payload.rows.map((row, index) => ({
      key: `${row.eventName}|${row.propKey}|${row.propValue}|${index}`,
      eventName: row.eventName,
      propKey: row.propKey,
      propValue: row.propValue,
      eventCount: row.eventCount,
      uv: row.uv,
      countWidth: barWidth(row.eventCount, maxCount)
    }))
  }
})

const activeRowCount = computed(() => {
  switch (subview.value) {
    case 'sources': return sourcesView.value?.rows.length ?? 0
    case 'visitors': return visitorsView.value?.rows.length ?? 0
    case 'pages': return pagesView.value?.rows.length ?? 0
    case 'events': return eventsView.value?.rows.length ?? 0
  }
})

const countFormatter = new Intl.NumberFormat('zh-CN')

function formatCount(value: number): string {
  return Number.isFinite(value) ? countFormatter.format(value) : '-'
}

function maxMetric(values: number[]): number {
  return values.reduce((max, value) => (Number.isFinite(value) && value > max ? value : max), 0)
}

function sumMetric<Row>(rows: Row[], pick: (row: Row) => number): number {
  return rows.reduce((total, row) => total + (Number.isFinite(pick(row)) ? pick(row) : 0), 0)
}

/** 行内占比水平条宽度：相对当前视图行最大值（对齐 workbench 趋势条手法）。 */
function barWidth(value: number, max: number): string {
  if (!Number.isFinite(value) || value <= 0 || max <= 0) return '0%'
  const percent = Math.min(100, (value / max) * 100)
  return `${Number(percent.toFixed(2))}%`
}
</script>

<template>
  <section class="behavior-analytics" :aria-busy="analyticsLoading ? 'true' : 'false'">
    <header class="behavior-analytics__header">
      <div class="behavior-analytics__heading">
        <p>租户流量</p>
        <h2>行为分析</h2>
      </div>

      <form class="behavior-analytics__filters" @submit.prevent="submitCustomPeriod">
        <select
          v-model="selectedPreset"
          class="ren-control behavior-analytics__preset"
          aria-label="行为分析统计周期"
          @change="changePreset"
        >
          <option v-for="option in presetOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
        <template v-if="selectedPreset === 'CUSTOM'">
          <input
            v-model="customFrom"
            class="ren-control ren-input behavior-analytics__date"
            type="date"
            :max="customUntil || undefined"
            aria-label="自定义开始日期"
          />
          <span class="behavior-analytics__date-separator" aria-hidden="true">至</span>
          <input
            v-model="customUntil"
            class="ren-control ren-input behavior-analytics__date"
            type="date"
            :min="customFrom || undefined"
            aria-label="自定义结束日期"
          />
          <button class="ren-button behavior-analytics__apply" type="submit">应用</button>
        </template>
        <select
          v-model="selectedModule"
          class="ren-control behavior-analytics__module"
          aria-label="行为分析模块范围"
          @change="changeModule"
        >
          <option v-for="option in moduleOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </form>
    </header>

    <p v-if="periodError" class="behavior-analytics__period-error" role="alert">
      {{ periodError }}
    </p>

    <div class="behavior-analytics__subviews" role="tablist" aria-label="行为分析视图">
      <button
        v-for="view in subviews"
        :id="`behavior-analytics-tab-${view.key}`"
        :key="view.key"
        class="behavior-analytics__tab"
        :class="{ 'is-active': subview === view.key }"
        type="button"
        role="tab"
        :aria-selected="subview === view.key ? 'true' : 'false'"
        :aria-controls="`behavior-analytics-panel-${view.key}`"
        @click="switchSubview(view.key)"
      >
        {{ view.label }}
      </button>
    </div>

    <div
      v-if="!featureEnabled"
      class="behavior-analytics__state behavior-analytics__state--disabled"
      aria-live="polite"
      role="status"
    >
      <strong>流量分析未启用</strong>
      <span>当前租户尚未开启流量分析，开通后即可查看行为报表</span>
    </div>
    <div
      v-else-if="analyticsLoading"
      class="behavior-analytics__state behavior-analytics__state--loading"
      aria-live="polite"
      role="status"
    >
      <strong>行为数据加载中</strong>
      <span>正在获取{{ activeSubviewLabel }}明细</span>
    </div>
    <div
      v-else-if="analyticsError || !analyticsData"
      class="behavior-analytics__state behavior-analytics__state--unavailable"
      aria-live="polite"
      role="status"
    >
      <strong>行为数据暂不可用</strong>
      <span>{{ analyticsError || '暂时无法读取当前视图明细' }}</span>
    </div>
    <div
      v-else-if="activeRowCount === 0"
      class="behavior-analytics__state behavior-analytics__state--empty"
      aria-live="polite"
      role="status"
    >
      <strong>尚无采集数据</strong>
      <span>当前周期内没有可展示的{{ activeSubviewLabel }}记录</span>
    </div>
    <template v-else>
      <section
        v-if="subview === 'sources' && sourcesView"
        id="behavior-analytics-panel-sources"
        class="behavior-analytics__report"
        role="tabpanel"
        aria-labelledby="behavior-analytics-tab-sources"
      >
        <h3>流量来源</h3>
        <dl class="behavior-analytics__totals" aria-label="来源视图合计">
          <div v-for="total in sourcesView.totals" :key="total.key">
            <dt>{{ total.label }}</dt>
            <dd>{{ total.value }}</dd>
          </div>
        </dl>
        <p class="behavior-analytics__note">UV 为行级精确口径，跨行相加的合计为近似值（同一访客跨来源会重复计数）。</p>
        <div class="behavior-analytics__table-scroll">
          <table>
            <caption class="behavior-analytics__sr-only">流量来源明细（渠道类型 / 来源主机 / UTM 来源）</caption>
            <thead>
              <tr>
                <th scope="col">渠道类型</th>
                <th scope="col">来源主机</th>
                <th scope="col">UTM 来源</th>
                <th scope="col">浏览量（PV）</th>
                <th scope="col">访问次数</th>
                <th scope="col">访客数（UV）</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in sourcesView.rows" :key="row.key">
                <th scope="row">{{ row.referrerKind }}</th>
                <td>{{ row.referrerHost }}</td>
                <td>{{ row.utmSource }}</td>
                <td>
                  <span class="behavior-analytics__metric" :aria-label="`浏览量 ${row.pv}`">
                    <span class="behavior-analytics__bar-track" aria-hidden="true">
                      <span class="behavior-analytics__bar" :style="{ width: row.pvWidth }"></span>
                    </span>
                    <span class="behavior-analytics__metric-value">{{ formatCount(row.pv) }}</span>
                  </span>
                </td>
                <td>{{ formatCount(row.sessions) }}</td>
                <td>{{ formatCount(row.uv) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section
        v-if="subview === 'visitors' && visitorsView"
        id="behavior-analytics-panel-visitors"
        class="behavior-analytics__report"
        role="tabpanel"
        aria-labelledby="behavior-analytics-tab-visitors"
      >
        <h3>访客画像</h3>
        <dl class="behavior-analytics__totals" aria-label="访客视图合计">
          <div v-for="total in visitorsView.totals" :key="total.key">
            <dt>{{ total.label }}</dt>
            <dd>{{ total.value }}</dd>
          </div>
        </dl>
        <p class="behavior-analytics__note">访客数为行级精确口径，跨行/跨日相加的合计为近似值（同一访客会重复计数）。</p>
        <div class="behavior-analytics__table-scroll">
          <table>
            <caption class="behavior-analytics__sr-only">访客明细（端 / 操作系统 / 设备 / 省份 / 新老访客）</caption>
            <thead>
              <tr>
                <th scope="col">端</th>
                <th scope="col">操作系统</th>
                <th scope="col">设备</th>
                <th scope="col">省份</th>
                <th scope="col">访客类型</th>
                <th scope="col">访客数</th>
                <th scope="col">访问次数</th>
                <th scope="col">浏览量（PV）</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in visitorsView.rows" :key="row.key">
                <th scope="row">{{ row.clientType }}</th>
                <td>{{ row.deviceOs }}</td>
                <td>{{ row.deviceClass }}</td>
                <td>{{ row.geoProvince }}</td>
                <td>{{ row.visitorCohort }}</td>
                <td>
                  <span class="behavior-analytics__metric" :aria-label="`访客数 ${row.visitors}`">
                    <span class="behavior-analytics__bar-track" aria-hidden="true">
                      <span class="behavior-analytics__bar" :style="{ width: row.visitorsWidth }"></span>
                    </span>
                    <span class="behavior-analytics__metric-value">{{ formatCount(row.visitors) }}</span>
                  </span>
                </td>
                <td>{{ formatCount(row.sessions) }}</td>
                <td>{{ formatCount(row.pv) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section
        v-if="subview === 'pages' && pagesView"
        id="behavior-analytics-panel-pages"
        class="behavior-analytics__report"
        role="tabpanel"
        aria-labelledby="behavior-analytics-tab-pages"
      >
        <h3>受访页</h3>
        <dl class="behavior-analytics__totals" aria-label="受访页视图合计">
          <div v-for="total in pagesView.totals" :key="total.key">
            <dt>{{ total.label }}</dt>
            <dd>{{ total.value }}</dd>
          </div>
        </dl>
        <p class="behavior-analytics__note">UV 为行级精确口径，跨行相加的合计为近似值（同一访客跨页面会重复计数）。</p>
        <div class="behavior-analytics__table-scroll">
          <table>
            <caption class="behavior-analytics__sr-only">受访页明细（页面 / 浏览 / 访客 / 平均停留 / 进入 / 退出）</caption>
            <thead>
              <tr>
                <th scope="col">页面</th>
                <th scope="col">浏览量（PV）</th>
                <th scope="col">访客数（UV）</th>
                <th scope="col">平均停留</th>
                <th scope="col">进入次数</th>
                <th scope="col">退出次数</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in pagesView.rows" :key="row.key">
                <th scope="row">{{ row.pageCode }}</th>
                <td>
                  <span class="behavior-analytics__metric" :aria-label="`浏览量 ${row.pv}`">
                    <span class="behavior-analytics__bar-track" aria-hidden="true">
                      <span class="behavior-analytics__bar" :style="{ width: row.pvWidth }"></span>
                    </span>
                    <span class="behavior-analytics__metric-value">{{ formatCount(row.pv) }}</span>
                  </span>
                </td>
                <td>{{ formatCount(row.uv) }}</td>
                <td>{{ row.averageActive }}</td>
                <td>{{ formatCount(row.entries) }}</td>
                <td>{{ formatCount(row.exits) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section
        v-if="subview === 'events' && eventsView"
        id="behavior-analytics-panel-events"
        class="behavior-analytics__report"
        role="tabpanel"
        aria-labelledby="behavior-analytics-tab-events"
      >
        <h3>自定义事件</h3>
        <dl class="behavior-analytics__totals" aria-label="事件视图合计">
          <div v-for="total in eventsView.totals" :key="total.key">
            <dt>{{ total.label }}</dt>
            <dd>{{ total.value }}</dd>
          </div>
        </dl>
        <p class="behavior-analytics__note">触发访客为行级精确口径，跨行相加的合计为近似值（同一访客会重复计数）。</p>
        <div class="behavior-analytics__table-scroll">
          <table>
            <caption class="behavior-analytics__sr-only">自定义事件明细（事件名 / 属性键值）</caption>
            <thead>
              <tr>
                <th scope="col">事件名</th>
                <th scope="col">属性键值</th>
                <th scope="col">次数</th>
                <th scope="col">触发访客（UV）</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in eventsView.rows" :key="row.key">
                <th scope="row">{{ row.eventName }}</th>
                <td>{{ row.propKey }}={{ row.propValue }}</td>
                <td>
                  <span class="behavior-analytics__metric" :aria-label="`次数 ${row.eventCount}`">
                    <span class="behavior-analytics__bar-track" aria-hidden="true">
                      <span class="behavior-analytics__bar" :style="{ width: row.countWidth }"></span>
                    </span>
                    <span class="behavior-analytics__metric-value">{{ formatCount(row.eventCount) }}</span>
                  </span>
                </td>
                <td>{{ formatCount(row.uv) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>
  </section>
</template>

<style scoped>
.behavior-analytics {
  display: grid;
  min-width: 0;
  gap: 20px;
  color: var(--rr-color-text);
}

.behavior-analytics__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  min-width: 0;
}

.behavior-analytics__heading {
  min-width: 0;
}

.behavior-analytics__heading p {
  margin: 0;
  color: var(--rr-color-text-muted);
  font-size: 12px;
  font-weight: 800;
}

.behavior-analytics__heading h2 {
  margin: 3px 0 0;
  overflow-wrap: anywhere;
  color: var(--rr-color-text-strong);
  font-size: 22px;
  line-height: 1.3;
}

.behavior-analytics__filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.behavior-analytics__preset {
  box-sizing: border-box;
  width: 132px;
  padding: 0 30px 0 10px;
}

.behavior-analytics__module {
  box-sizing: border-box;
  width: 120px;
  padding: 0 30px 0 10px;
}

.behavior-analytics__date {
  width: 142px;
}

.behavior-analytics__date-separator {
  color: var(--rr-color-text-muted);
  font-size: 13px;
}

.behavior-analytics__apply {
  padding: 0 14px;
}

.behavior-analytics__period-error {
  margin: -12px 0 0;
  color: var(--rr-color-danger);
  font-size: 13px;
  text-align: right;
}

.behavior-analytics__subviews {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  border-bottom: 1px solid var(--rr-color-border);
}

.behavior-analytics__tab {
  min-height: 36px;
  border: 1px solid var(--rr-color-border);
  border-bottom: 0;
  border-radius: 8px 8px 0 0;
  background: var(--rr-color-surface);
  color: var(--rr-color-text-muted);
  padding: 0 16px;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.behavior-analytics__tab:hover {
  border-color: var(--rr-color-border-strong);
  color: var(--rr-color-text);
}

.behavior-analytics__tab.is-active {
  border-color: var(--rr-color-primary-border);
  background: var(--rr-color-primary-soft);
  color: var(--rr-color-primary);
}

.behavior-analytics__tab:focus-visible {
  outline: 3px solid var(--rr-color-primary-border);
  outline-offset: 2px;
}

.behavior-analytics__state {
  box-sizing: border-box;
  display: grid;
  align-content: center;
  min-height: 88px;
  gap: 4px;
  border-left: 3px solid var(--rr-color-border-strong);
  background: var(--rr-color-surface-subtle);
  padding: 16px 18px;
  color: var(--rr-color-text);
}

.behavior-analytics__state strong {
  color: var(--rr-color-text-strong);
  font-size: 14px;
}

.behavior-analytics__state span {
  overflow-wrap: anywhere;
  color: var(--rr-color-text-muted);
  font-size: 13px;
}

.behavior-analytics__state--loading {
  border-color: var(--rr-color-primary);
  background: var(--rr-color-primary-soft);
}

.behavior-analytics__state--unavailable {
  border-color: var(--rr-color-danger);
  background: var(--rr-color-danger-soft);
}

.behavior-analytics__state--empty {
  border-color: var(--rr-color-primary);
}

.behavior-analytics__report {
  display: grid;
  min-width: 0;
  gap: 12px;
}

.behavior-analytics__report h3 {
  margin: 0;
  color: var(--rr-color-text-strong);
  font-size: 15px;
  line-height: 1.4;
}

.behavior-analytics__totals {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin: 0;
  border-top: 1px solid var(--rr-color-border);
  border-bottom: 1px solid var(--rr-color-border);
}

.behavior-analytics__totals > div {
  min-width: 0;
  min-height: 76px;
  padding: 12px 14px;
}

.behavior-analytics__totals > div + div {
  border-left: 1px solid var(--rr-color-border);
}

.behavior-analytics__totals dt {
  overflow-wrap: anywhere;
  color: var(--rr-color-text-muted);
  font-size: 12px;
  font-weight: 700;
  line-height: 1.4;
}

.behavior-analytics__totals dd {
  margin: 6px 0 0;
  overflow-wrap: anywhere;
  color: var(--rr-color-text-strong);
  font-size: 20px;
  font-variant-numeric: tabular-nums;
  font-weight: 800;
  line-height: 1.2;
}

.behavior-analytics__note {
  margin: 0;
  color: var(--rr-color-text-muted);
  font-size: 12px;
}

.behavior-analytics__table-scroll {
  min-width: 0;
  overflow-x: auto;
}

.behavior-analytics__table-scroll table {
  width: 100%;
  min-width: 680px;
  border-collapse: collapse;
  table-layout: fixed;
  color: var(--rr-color-text);
  font-size: 13px;
}

.behavior-analytics__table-scroll th,
.behavior-analytics__table-scroll td {
  min-width: 0;
  border-bottom: 1px solid var(--rr-color-border);
  padding: 10px 12px;
  overflow-wrap: anywhere;
  font-variant-numeric: tabular-nums;
  text-align: right;
  vertical-align: middle;
}

.behavior-analytics__table-scroll thead th {
  border-top: 1px solid var(--rr-color-border);
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text-muted);
  font-size: 12px;
  font-weight: 800;
}

.behavior-analytics__table-scroll tbody th[scope='row'] {
  color: var(--rr-color-text-strong);
  font-weight: 700;
  text-align: left;
}

.behavior-analytics__table-scroll td:first-child,
.behavior-analytics__table-scroll th:first-child {
  text-align: left;
}

.behavior-analytics__metric {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 56px;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.behavior-analytics__bar-track {
  position: relative;
  display: block;
  width: 100%;
  height: 7px;
  overflow: hidden;
  border-radius: 2px;
  background: var(--rr-color-primary-soft);
}

.behavior-analytics__bar {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--rr-color-primary);
  transition: width 0.2s ease;
}

.behavior-analytics__metric-value {
  color: var(--rr-color-text);
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  text-align: right;
}

.behavior-analytics__sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  clip-path: inset(50%);
  white-space: nowrap;
}

@media (max-width: 960px) {
  .behavior-analytics__header {
    align-items: flex-start;
    flex-direction: column;
  }

  .behavior-analytics__filters {
    width: 100%;
    justify-content: flex-start;
  }

  .behavior-analytics__totals {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .behavior-analytics__totals > div + div {
    border-left: 0;
  }

  .behavior-analytics__totals > div:nth-child(n + 3) {
    border-top: 1px solid var(--rr-color-border);
  }
}

@media (max-width: 600px) {
  .behavior-analytics {
    gap: 16px;
  }

  .behavior-analytics__preset,
  .behavior-analytics__module,
  .behavior-analytics__date,
  .behavior-analytics__apply {
    width: 100%;
  }

  .behavior-analytics__date-separator {
    display: none;
  }

  .behavior-analytics__totals {
    grid-template-columns: 1fr;
  }

  .behavior-analytics__totals > div:nth-child(n + 2) {
    border-top: 1px solid var(--rr-color-border);
  }
}
</style>
